/**
 * Controle de "já vi as novidades desta versão do plugin".
 *
 * A versão sozinha não conta história: quem administra a empresa vê "v1.3.31"
 * no card e não sabe o que isso mudou. O plugin declara as novidades no
 * metadata (`changelog`), e aqui guardamos qual versão cada pessoa já leu, para
 * o card poder avisar que há algo novo sem interromper ninguém.
 *
 * Fica no navegador de propósito: é preferência de leitura de uma pessoa, não
 * dado da empresa — não justifica coluna no banco nem requisição extra. A chave
 * inclui o usuário porque a mesma máquina é compartilhada em muitos
 * atendimentos.
 */

const PREFIXO = "mk:plugin-novidades-vistas";

function chave(userId, pluginId) {
  return `${PREFIXO}:${userId || "anon"}:${pluginId}`;
}

/** Storage injetável — o padrão é o do navegador. */
function storagePadrao() {
  try {
    return typeof window !== "undefined" ? window.localStorage : null;
  } catch (e) {
    return null; // navegador com storage bloqueado
  }
}

/** Versão do plugin que este usuário já leu, ou null. */
export function versaoVista(userId, pluginId, storage = storagePadrao()) {
  if (!pluginId || !storage) return null;
  try {
    return storage.getItem(chave(userId, pluginId)) || null;
  } catch (e) {
    return null;
  }
}

/** Marca a versão como lida. Chamado ao abrir a ficha do plugin. */
export function marcarVersaoVista(userId, pluginId, versao, storage = storagePadrao()) {
  if (!pluginId || !versao || !storage) return;
  try {
    storage.setItem(chave(userId, pluginId), String(versao));
  } catch (e) {
    /* storage cheio ou bloqueado: o selo reaparece, e só */
  }
}

/**
 * Registra silenciosamente a versão dos plugins ainda desconhecidos.
 *
 * Chamado quando a lista carrega. Sem isto, o selo dependeria de a pessoa ter
 * aberto a ficha alguma vez — quem nunca abriu jamais seria avisado de uma
 * atualização, que é justamente quem mais precisa do aviso. Assim a primeira
 * visita apenas anota o que existe hoje (sem selo, porque não há nada de novo a
 * contar), e qualquer atualização a partir dali aparece.
 */
export function registrarVersoesConhecidas(plugins, userId, storage = storagePadrao()) {
  if (!Array.isArray(plugins) || !storage) return;
  plugins.forEach(plugin => {
    const pluginId = plugin?.id || plugin?.channel;
    if (!pluginId || !plugin.version) return;
    if (versaoVista(userId, pluginId, storage)) return;
    marcarVersaoVista(userId, pluginId, plugin.version, storage);
  });
}

/**
 * Há novidade a mostrar para este usuário?
 *
 * Só quando existe o que ler: plugin sem changelog nunca acusa novidade, senão
 * o selo viraria enfeite permanente em quem não declarou nada. Sem registro
 * anterior também não acusa — é a primeira vez que essa pessoa vê o plugin.
 */
export function temNovidade(plugin, userId, storage = storagePadrao()) {
  if (!plugin || !plugin.version) return false;
  if (!Array.isArray(plugin.changelog) || plugin.changelog.length === 0) return false;

  const pluginId = plugin.id || plugin.channel;
  const vista = versaoVista(userId, pluginId, storage);
  if (!vista) return false;

  return vista !== String(plugin.version);
}
